import { getSupabase } from '@/lib/supabase';
import type { GameDefinition } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { decodeRowToGameDefinition, type GameDefinitionRow } from '../codec';
import { getUserSyncState, setUserLastDownloadedAt } from '../state';

export interface DownloadResult {
  definitions: GameDefinition[];
}

export async function downloadRemoteGameDefinitions(userId: UserId): Promise<DownloadResult> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync game definitions');
  }

  const { lastDownloadedAt = 0 } = await getUserSyncState(userId, 'game_definitions');
  // Download all game definitions that were created/updated/deleted after lastDownloadedAt
  const { data, error } = await client
    .from('game_definitions')
    .select<'*', GameDefinitionRow>('*')
    .eq('user_id', userId)
    .or(
      `created_at.gt.${lastDownloadedAt},updated_at.gt.${lastDownloadedAt},deleted_at.gt.${lastDownloadedAt}`,
    )
    .order('updated_at', { ascending: true })
    .limit(1000);
  if (error) throw error;
  const definitions = (data ?? []).map((row) => decodeRowToGameDefinition(row));
  if (definitions.length > 0) {
    const maxTs = Math.max(
      ...definitions.map((d) => Math.max(d.createdAt, d.updatedAt, d.deletedAt ?? 0)),
      0,
    );
    await setUserLastDownloadedAt(userId, 'game_definitions', maxTs);
  }
  return { definitions };
}
